import React, { useState, useEffect } from "react";
import {
	Modal,
	Form,
	Input,
	Select,
	DatePicker,
	Button,
	Table,
	Tag,
	message,
	Row,
	Col,
	Typography,
} from "antd";
import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import useAxios from "../../utilities/useAxios";
import dayjs from "dayjs";
import useIsMobile from "../../hooks/useIsMobile";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const AddTransferRecordModal = ({ visible, onCancel, onSuccess }) => {
	const isMobile = useIsMobile();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [members, setMembers] = useState([]);
	const [selectedMembers, setSelectedMembers] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [searchCategory, setSearchCategory] = useState("name");

	useEffect(() => {
		if (visible) {
			fetchMembers();
		}
	}, [visible]);

	const fetchMembers = async () => {
		try {
			const response = await useAxios.get(
				"/users/all-users/"
			);
			if (response.status === 200) {
				setMembers(response.data);
			}
		} catch (error) {
			message.error("회원 목록을 불러오는데 실패했습니다.");
		}
	};

	const handleSearch = (value) => {
		setSearchText(value);
	};

	const handleCategoryChange = (value) => {
		setSearchCategory(value);
	};

	const handleMemberSelection = (selectedRowKeys, selectedRows) => {
		// Get all currently selected members (including those not in current filtered view)
		const allSelectedMembers = [...selectedMembers];

		// Update the selection for the current filtered members
		selectedRows.forEach((selectedRow) => {
			const existingIndex = allSelectedMembers.findIndex(
				(member) => member.id === selectedRow.id
			);
			if (existingIndex === -1) {
				// Add new selection
				allSelectedMembers.push(selectedRow);
			}
		});

		// Remove deselected members from current filtered view
		const currentFilteredIds = filteredMembers.map(
			(member) => member.id
		);
		const finalSelectedMembers = allSelectedMembers.filter(
			(member) => {
				// Keep if it's in the current filtered view and selected
				if (currentFilteredIds.includes(member.id)) {
					return selectedRowKeys.includes(
						member.id
					);
				}
				// Keep if it's not in current filtered view (preserve previous selections)
				return true;
			}
		);

		setSelectedMembers(finalSelectedMembers);
	};

	const handleSubmit = async (values) => {
		if (selectedMembers.length === 0) {
			message.error("최소 한 명의 회원을 선택해주세요.");
			return;
		}

		try {
			setLoading(true);
			const submitData = {
				...values,
				user_ids: selectedMembers.map(
					(member) => member.id
				),
				transfer_date:
					values.transfer_date.format(
						"YYYY-MM-DD"
					),
			};

			const response = await useAxios.post(
				"/users/add-transfer-record/",
				submitData
			);

			if (response.status === 201) {
				message.success(
					`${selectedMembers.length}명의 회원에게 이명 기록이 추가되었습니다.`
				);
				form.resetFields();
				setSelectedMembers([]);
				if (onSuccess) {
					onSuccess();
				}
			}
		} catch (error) {
			message.error("이명 기록 추가에 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "입교인":
				return "blue";
			case "학습인":
				return "green";
			case "유아세례교인":
				return "orange";
			case "실종교인":
				return "red";
			case "별세교인":
				return "default";
			default:
				return "default";
		}
	};

	const getClassificationColor = (classification) => {
		switch (classification) {
			case "성도":
				return "blue";
			case "교직원":
				return "green";
			case "교역자":
				return "purple";
			case "은퇴목사":
				return "orange";
			default:
				return "default";
		}
	};

	const getTransferColor = (type) => {
		switch (type) {
			case "전입":
				return "green";
			case "전출":
				return "red";
			default:
				return "default";
		}
	};

	const filteredMembers = members.filter((member) => {
		if (searchText) {
			const searchValue =
				member[searchCategory]
					?.toString()
					.toLowerCase() || "";
			if (!searchValue.includes(searchText.toLowerCase())) {
				return false;
			}
		}
		return true;
	});

	const memberColumns = [
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
			render: (text) => text || "-",
		},
		{
			title: "생년월일",
			dataIndex: "birthdate",
			key: "birthdate",
			render: (text) =>
				text ? dayjs(text).format("YYYY-MM-DD") : "-",
		},
		{
			title: "성별",
			dataIndex: "gender",
			key: "gender",
			render: (text) => text || "-",
		},
		{
			title: "분류",
			dataIndex: "classification_name",
			key: "classification_name",
			render: (text) => (
				<Tag color={getClassificationColor(text)}>
					{text || "-"}
				</Tag>
			),
		},
		{
			title: "상태",
			dataIndex: "status_name",
			key: "status_name",
			render: (text) => (
				<Tag color={getStatusColor(text)}>
					{text || "-"}
				</Tag>
			),
		},
		{
			title: "소속",
			dataIndex: "affiliated_group",
			key: "affiliated_group",
			render: (text) => text || "-",
		},
	];

	return (
		<Modal
			title={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
					}}>
					<PlusOutlined />
					<span>이명 기록 추가</span>
				</div>
			}
			open={visible}
			onCancel={onCancel}
			footer={null}
			width="90%"
			style={{ top: 20 }}>
			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				initialValues={{
					transfer_date: dayjs(),
				}}>
				{/* Member Selection Section */}
				<div style={{ marginBottom: 24 }}>
					<Title level={4}>
						<UserOutlined /> 회원 선택
					</Title>
					<Text type="secondary">
						이명 기록을 추가할 회원을
						선택하세요.
					</Text>

					{/* Search Section */}
					<Row
						gutter={[16, 16]}
						style={{
							marginTop: 16,
							marginBottom: 16,
						}}>
						<Col xs={24} sm={12} md={6}>
							<Select
								defaultValue="name"
								style={{
									width: "100%",
								}}
								onChange={
									handleCategoryChange
								}
								options={[
									{
										value: "name",
										label: "이름",
									},
									{
										value: "phone_number",
										label: "연락처",
									},
									{
										value: "affiliated_group",
										label: "소속",
									},
								]}
							/>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Input.Search
								placeholder="검색어를 입력하세요"
								allowClear
								onSearch={
									handleSearch
								}
								style={{
									width: "100%",
								}}
							/>
						</Col>
					</Row>

					{/* Member Table */}
					<Table
						columns={memberColumns}
						dataSource={filteredMembers}
						rowKey="id"
						rowSelection={{
							type: "checkbox",
							selectedRowKeys:
								selectedMembers.map(
									(
										member
									) =>
										member.id
								),
							onChange: handleMemberSelection,
						}}
						pagination={{
							pageSize: 10,
							showSizeChanger: true,
							showQuickJumper: true,
						}}
						scroll={{ y: 300 }}
					/>

					{selectedMembers.length > 0 && (
						<div style={{ marginTop: 16 }}>
							<Text strong>
								선택된 회원:{" "}
								{
									selectedMembers.length
								}
								명
							</Text>
							<div
								style={{
									marginTop: 8,
								}}>
								{selectedMembers.map(
									(
										member
									) => (
										<Tag
											key={
												member.id
											}
											style={{
												marginBottom: 4,
											}}>
											{
												member.name
											}
										</Tag>
									)
								)}
							</div>
						</div>
					)}
				</div>

				{/* Transfer Information Section */}
				<div style={{ marginBottom: 24 }}>
					<Title level={4}>
						<PlusOutlined /> 이명 정보
					</Title>
					<Text type="secondary">
						이명 정보를 입력하세요.
					</Text>

					<Row
						gutter={[16, 16]}
						style={{ marginTop: 16 }}>
						<Col xs={24} sm={12} md={6}>
							<Form.Item
								name="transfer_type"
								label="이명 유형"
								rules={[
									{
										required: true,
										message: "이명 유형을 선택하세요",
									},
								]}>
								<Select placeholder="이명 유형 선택">
									<Option value="전입">
										<Tag color="green">
											전입
										</Tag>
									</Option>
									<Option value="전출">
										<Tag color="red">
											전출
										</Tag>
									</Option>
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Form.Item
								name="transfer_date"
								label="이명일"
								rules={[
									{
										required: true,
										message: "이명일을 선택하세요",
									},
								]}>
								<DatePicker
									inputReadOnly={isMobile}
									style={{
										width: "100%",
									}}
									format="YYYY-MM-DD"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Form.Item
								name="corressponding_church"
								label="상대교회"
								rules={[
									{
										required: true,
										message: "상대교회를 입력하세요",
									},
								]}>
								<Input placeholder="상대교회 입력" />
							</Form.Item>
						</Col>
					</Row>

					<Form.Item
						name="reason"
						label="이명사유"
						rules={[
							{
								required: true,
								message: "이명사유를 입력하세요",
							},
						]}>
						<TextArea
							rows={4}
							placeholder="이명사유를 상세히 입력하세요"
						/>
					</Form.Item>
				</div>

				{/* Submit Button */}
				<div style={{ textAlign: "right" }}>
					<Button
						type="primary"
						htmlType="submit"
						loading={loading}
						icon={<PlusOutlined />}
						disabled={
							selectedMembers.length ===
							0
						}>
						이명 기록 추가
					</Button>
				</div>
			</Form>
		</Modal>
	);
};

export default AddTransferRecordModal;
